/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { MonthRecord, fetchPfDetails } from "./annualPf.service";

const emptyTotals = {
  employeePF: "0.00",
  voluntaryPF: "0.00",
  pensionContr: "0.00",
  employerPF: "0.00",
};

const AnnualPf: React.FC = () => {
  const [year, setYear] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [data, setData] = useState<MonthRecord[]>([]);
  const [totals, setTotals] = useState(emptyTotals);

  const isDisabled = year.trim().length === 0;

  const handleYearChange = (value: string) => {
    setYear(value.replace(/\D/g, "").slice(0, 4));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!year.trim()) return;

    setLoading(true);
    setApiError("");
    setSubmitted(false);
    try {
      const result = await fetchPfDetails(year.trim());
      setData(result.months);
      setTotals(result.totals);
    } catch (err: any) {
      const message = err?.message || "Something went wrong";
      setApiError(message);
      setData([]);
      setTotals(emptyTotals);
      toast.error(message);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="page-wrapper">
      <style>{pfStyles}</style>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Annual PF Details</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={all_routes.services.list}>Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Annual PF Details
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="pf-portal-banner mb-3">
          <span className="pf-portal-badge">PF PORTAL</span>
          <h4 className="pf-portal-title">Employer/Employee Contribution</h4>
          <p className="pf-portal-sub mb-0">Contribution Summary</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card pf-filter-card mb-3">
            <div className="card-body">
              <p className="pf-eyebrow mb-2">Choose Year</p>
              <div className="pf-input-row">
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-control pf-year-input"
                  placeholder="e.g. 2024"
                  maxLength={4}
                  value={year}
                  onChange={(e) => handleYearChange(e.target.value)}
                />
                <button
                  type="submit"
                  className="btn btn-primary pf-submit-btn"
                  disabled={isDisabled || loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    "SUBMIT"
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {submitted && (
          <>
            <div className="pf-section-divider">
              <span />
              <em>Total contribution amount</em>
              <span />
            </div>

            <div className="row g-3 mb-3">
              <div className="col-sm-6 col-xl-3">
                <div className="pf-tile pf-tile-emp">
                  <div className="pf-tile-icon">👤</div>
                  <p className="pf-tile-label">Employee PF contribution</p>
                  <h5 className="pf-tile-value">₹ {totals.employeePF}</h5>
                </div>
              </div>
              <div className="col-sm-6 col-xl-3">
                <div className="pf-tile pf-tile-vol">
                  <div className="pf-tile-icon pf-tile-icon-vol">👤</div>
                  <p className="pf-tile-label">Voluntary PF contribution</p>
                  <h5 className="pf-tile-value pf-tile-value-vol">₹ {totals.voluntaryPF}</h5>
                </div>
              </div>
              <div className="col-sm-6 col-xl-3">
                <div className="pf-tile pf-tile-pen">
                  <div className="pf-tile-icon pf-tile-icon-pen">🏦</div>
                  <p className="pf-tile-label">Employer Pension contribution</p>
                  <h5 className="pf-tile-value pf-tile-value-pen">₹ {totals.pensionContr}</h5>
                </div>
              </div>
              <div className="col-sm-6 col-xl-3">
                <div className="pf-tile pf-tile-empr">
                  <div className="pf-tile-icon pf-tile-icon-empr">🏢</div>
                  <p className="pf-tile-label">Employer PF contribution</p>
                  <h5 className="pf-tile-value pf-tile-value-empr">₹ {totals.employerPF}</h5>
                </div>
              </div>
            </div>

            <div className="card pf-table-card">
              <div className="pf-table-top">
                <span className="pf-table-dot" />
                <span>Monthly Details</span>
              </div>
              <div className="table-responsive">
                {apiError ? (
                  <div className="pf-empty">
                    <div className="pf-empty-icon">⚠️</div>
                    <p className="mb-0">{apiError}</p>
                  </div>
                ) : data.length > 0 ? (
                  <table className="table pf-table mb-0">
                    <thead>
                      <tr>
                        <th className="text-start">Month</th>
                        <th className="text-end">Emp. PF contr.</th>
                        <th className="text-end">Empr. PF contr.</th>
                        <th className="text-end">Vol. PF contr.</th>
                        <th className="text-end">Pension</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item, index) => (
                        <tr key={`${item.month}-${index}`} className={index % 2 === 0 ? "even" : "odd"}>
                          <td className="text-start pf-month">{item.month}</td>
                          <td className="text-end">{item.employeePF}</td>
                          <td className="text-end">{item.employerPF}</td>
                          <td className={`text-end ${item.voluntaryPF !== "0.00" ? "pf-vol-ok" : ""}`}>
                            {item.voluntaryPF}
                          </td>
                          <td className="text-end">{item.pensionContr}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="pf-empty">
                    <div className="pf-empty-icon">📭</div>
                    <p className="mb-0">No records found for {year}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!submitted && (
          <div className="pf-hint">
            <h6>How to use</h6>
            <p className="mb-0">
              Enter a 4-digit financial year (e.g. 2024 or 2025) and click SUBMIT to view your
              monthly PF contribution breakdown.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const pfStyles = `
.pf-portal-banner {
  background: var(--primary, #A93226);
  border-radius: 16px;
  padding: 22px 24px;
  color: #fff;
}
.pf-portal-badge {
  display: inline-block;
  background: rgba(255,255,255,0.92);
  color: var(--primary, #A93226);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  border-radius: 8px;
  padding: 3px 10px;
  margin-bottom: 10px;
}
.pf-portal-title { color: #fff; font-weight: 700; margin-bottom: 4px; }
.pf-portal-sub { color: rgba(255,255,255,0.82); font-size: 13px; }
.pf-filter-card { border: 1px solid #eee; box-shadow: 0 2px 12px rgba(0,0,0,0.04); border-radius: 16px; }
.pf-eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: 1px;
  text-transform: uppercase; color: #6b7280;
}
.pf-input-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pf-year-input {
  flex: 1; min-width: 180px; max-width: 320px;
  font-weight: 700; letter-spacing: 2px; font-size: 16px;
}
.pf-submit-btn { height: 46px; min-width: 120px; letter-spacing: 1px; font-weight: 700; }
.pf-section-divider {
  display: flex; align-items: center; gap: 12px; margin: 8px 0 16px;
}
.pf-section-divider span { flex: 1; height: 1px; background: #e5e7eb; }
.pf-section-divider em {
  font-style: normal; font-size: 11px; font-weight: 700;
  letter-spacing: 2px; color: #6b7280; white-space: nowrap;
}
.pf-tile {
  background: #fff; border: 1px solid #eee; border-radius: 16px;
  padding: 16px; height: 100%; box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.pf-tile-emp { border-left: 3px solid var(--primary, #A93226); }
.pf-tile-vol { border-left: 3px solid #16a34a; }
.pf-tile-pen { border-left: 3px solid #7c3aed; }
.pf-tile-empr { border-left: 3px solid #f59e0b; }
.pf-tile-icon {
  width: 34px; height: 34px; border-radius: 8px;
  background: rgba(169, 50, 38, 0.12);
  display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
}
.pf-tile-icon-vol { background: rgba(22, 163, 74, 0.12); }
.pf-tile-icon-pen { background: rgba(124, 58, 237, 0.12); }
.pf-tile-icon-empr { background: rgba(245, 158, 11, 0.15); }
.pf-tile-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.6px;
  text-transform: uppercase; color: #6b7280; margin-bottom: 6px;
}
.pf-tile-value { font-weight: 700; color: var(--primary, #A93226); margin: 0; }
.pf-tile-value-vol { color: #16a34a; }
.pf-tile-value-pen { color: #7c3aed; }
.pf-tile-value-empr { color: #d97706; }
.pf-table-card { border: 1px solid #eee; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
.pf-table-top {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 16px; background: rgba(169, 50, 38, 0.08);
  border-bottom: 1px solid #eee; font-size: 11px; font-weight: 700;
  letter-spacing: 1px; text-transform: uppercase; color: var(--primary, #A93226);
}
.pf-table-dot { width: 8px; height: 8px; border-radius: 4px; background: var(--primary, #A93226); }
.pf-table thead th {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px;
  color: #6b7280; font-weight: 700; background: #f8fafc; white-space: nowrap;
}
.pf-table td { font-size: 13px; color: #374151; white-space: nowrap; }
.pf-table .even { background: #f9fafb; }
.pf-table .odd { background: #fff; }
.pf-month { font-weight: 700; color: var(--primary, #A93226); }
.pf-vol-ok { font-weight: 700; color: #16a34a; }
.pf-empty { text-align: center; padding: 36px 16px; color: #6b7280; }
.pf-empty-icon { font-size: 28px; margin-bottom: 8px; }
.pf-hint {
  border: 1px solid #eee; border-left: 3px solid var(--primary, #A93226);
  border-radius: 16px; padding: 16px; background: #fff;
}
.pf-hint h6 { color: var(--primary, #A93226); font-weight: 700; margin-bottom: 8px; }
.pf-hint p { color: #6b7280; font-size: 14px; line-height: 1.6; }
`;

export default AnnualPf;
